import type {
    Client,
    ClientLifecycle,
    ClientLifecycleStatus,
    ClientDeliveryStage,
    ClientPipelineStage,
    ClientCommercialStage,
} from '~/types/models';

function normalizeText(value: unknown): string {
    return String(value || '').trim();
}

function organizationLabel(client: Pick<Client, 'companyName' | 'businessName'>): boolean {
    return Boolean(normalizeText(client.companyName) || normalizeText(client.businessName));
}

export function deriveClientCommercialStageFromClientLifecycle(lifecycle?: ClientLifecycle | null): ClientCommercialStage {
    const explicit = normalizeText(lifecycle?.commercialStage) as ClientCommercialStage;
    if (explicit) return explicit;

    switch (lifecycle?.status as ClientLifecycleStatus | undefined) {
        case 'qualified':
        case 'opportunity':
        case 'proposal_sent':
            return 'opportunity';
        case 'won':
        case 'onboarding':
        case 'active':
        case 'paused':
        case 'completed':
            return 'client';
        case 'past':
        case 'lost':
        case 'archived':
            return 'past_client';
        default:
            return 'lead';
    }
}

export function derivePipelineStageFromClientLifecycle(lifecycle?: ClientLifecycle | null): ClientPipelineStage {
    const explicit = normalizeText(lifecycle?.pipelineStage) as ClientPipelineStage;
    if (explicit) return explicit;

    switch (lifecycle?.status as ClientLifecycleStatus | undefined) {
        case 'qualified':
            return 'qualified';
        case 'opportunity':
            return 'negotiation';
        case 'proposal_sent':
            return 'proposal_sent';
        case 'won':
        case 'onboarding':
        case 'active':
        case 'paused':
        case 'completed':
        case 'past':
            return 'won';
        case 'lost':
        case 'archived':
            return 'lost';
        default:
            return 'new_enquiry';
    }
}

export function deriveDeliveryStageFromClientLifecycle(lifecycle?: ClientLifecycle | null): ClientDeliveryStage {
    const explicit = normalizeText(lifecycle?.deliveryStage) as ClientDeliveryStage;
    if (explicit) return explicit;

    switch (lifecycle?.status as ClientLifecycleStatus | undefined) {
        case 'onboarding':
            return 'onboarding';
        case 'active':
            return 'active';
        case 'paused':
            return 'paused';
        case 'completed':
        case 'past':
        case 'archived':
            return 'complete';
        default:
            return 'pre_engagement';
    }
}

export type ClientWorkspaceOnboardingState = {
    onboardingSkipped: boolean;
    readyToStartOnboarding: boolean;
};

export type ClientWorkspaceOwnershipScope = {
    clientId?: string;
    linkedProjectIds: string[];
};

export function deriveClientWorkspaceOnboardingState(input: {
    clientLifecycle?: ClientLifecycle | null;
    hasActiveRun?: boolean;
}): ClientWorkspaceOnboardingState {
    const onboardingSkipped = Boolean(input.clientLifecycle?.onboardingSkippedAt);
    const pipelineStage = derivePipelineStageFromClientLifecycle(input.clientLifecycle);

    return {
        onboardingSkipped,
        readyToStartOnboarding: pipelineStage === 'won' && !input.hasActiveRun && !onboardingSkipped,
    };
}

export function buildClientWorkspaceOwnershipScope(input: {
    clientId?: string | null;
    linkedProjectIds?: string[] | null;
}): ClientWorkspaceOwnershipScope {
    const clientId = normalizeText(input.clientId) || undefined;

    return {
        clientId,
        linkedProjectIds: Array.from(new Set((Array.isArray(input.linkedProjectIds) ? input.linkedProjectIds : [])
            .map((projectId) => normalizeText(projectId))
            .filter(Boolean))),
    };
}

export function clientWorkspaceOwnsEntity(input: {
    scope: ClientWorkspaceOwnershipScope;
    entityClientId?: unknown;
    entityProjectId?: unknown;
}): boolean {
    const entityClientId = normalizeText(input.entityClientId);
    const projectId = normalizeText(input.entityProjectId);
    if (entityClientId && entityClientId !== input.scope.clientId) {
        return false;
    }

    return (Boolean(input.scope.clientId) && entityClientId === input.scope.clientId)
        || (Boolean(projectId) && input.scope.linkedProjectIds.includes(projectId));
}

export type ClientWorkspaceDetailContext = {
    ownerType: 'client';
    clientId?: string;
    clientSlug?: string;
    routeKind: 'client';
};

export function resolveClientWorkspaceDetailContext(input: {
    client?: Pick<Client, 'id' | 'slug' | 'lifecycle'> | null;
    routeClientSlug?: string | null;
}): ClientWorkspaceDetailContext {
    const clientId = normalizeText(input.client?.id) || undefined;
    const clientSlug = normalizeText(input.client?.slug) || undefined;

    return {
        ownerType: 'client',
        clientId,
        clientSlug,
        routeKind: 'client',
    };
}
